import React, {createContext, useContext, useReducer, useMemo} from 'react';
import PropTypes from 'prop-types';
import {VIEW_STATES} from 'constants/AAConstants';
import {determineViewState} from '../services/viewStateResolver';

export const ACTION_TYPES = {
  SET_LOADING: 'SET_LOADING',
  SET_CONSENT_DATA: 'SET_CONSENT_DATA',
  SET_VIEW_STATE: 'SET_VIEW_STATE',
  SET_ERROR: 'SET_ERROR',
  RESET_ERROR: 'RESET_ERROR',
  SET_SHOW_UPLOAD_OPTION: 'SET_SHOW_UPLOAD_OPTION',
  SET_HAS_EXISTING_CONSENT: 'SET_HAS_EXISTING_CONSENT',
};

const initialState = {
  viewState: VIEW_STATES.LOADING,
  consent: {
    status: null,
    processingStatus: null,
    errorMessage: null,
    errorCode: null,
  },
  isLoading: true,
  hasExistingConsent: false,
  showUploadOption: false,
};

const aaReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_LOADING: {
      const newState = {
        ...state,
        isLoading: action.payload,
      };
      const newViewState = determineViewState(
        newState.consent,
        newState.hasExistingConsent,
        newState.isLoading,
        newState.showUploadOption
      );
      return {
        ...newState,
        viewState: newViewState,
      };
    }

    case ACTION_TYPES.SET_CONSENT_DATA: {
      const {status, processingStatus, errorMessage, errorCode} = action.payload;
      const newConsent = {
        status: status || state.consent.status,
        processingStatus: processingStatus || state.consent.processingStatus,
        errorMessage: errorMessage !== undefined ? errorMessage : state.consent.errorMessage,
        errorCode: errorCode !== undefined ? errorCode : state.consent.errorCode,
      };

      return {
        ...state,
        consent: newConsent,
      };
    }

    case ACTION_TYPES.SET_VIEW_STATE:
      return {
        ...state,
        viewState: action.payload,
      };

    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        consent: {
          ...state.consent,
          errorMessage: action.payload.errorMessage,
          errorCode: action.payload.errorCode,
        },
      };

    case ACTION_TYPES.RESET_ERROR:
      return {
        ...state,
        consent: {
          ...state.consent,
          errorMessage: null,
          errorCode: null,
        },
        showUploadOption: false,
      };

    case ACTION_TYPES.SET_SHOW_UPLOAD_OPTION:
      return {
        ...state,
        showUploadOption: action.payload,
        viewState: action.payload ? VIEW_STATES.FAILED : state.viewState,
      };

    case ACTION_TYPES.SET_HAS_EXISTING_CONSENT: {
      const newState = {
        ...state,
        hasExistingConsent: action.payload,
      };
      const newViewState = determineViewState(
        newState.consent,
        newState.hasExistingConsent,
        newState.isLoading,
        newState.showUploadOption
      );
      return {
        ...newState,
        viewState: newViewState,
      };
    }

    default:
      return state;
  }
};

const AAStateContext = createContext(undefined);
const AADispatchContext = createContext(undefined);

export const AAProvider = ({children}) => {
  const [state, dispatch] = useReducer(aaReducer, initialState);

  const dispatchValue = useMemo(() => dispatch, []);

  return (
    <AAStateContext.Provider value={state}>
      <AADispatchContext.Provider value={dispatchValue}>{children}</AADispatchContext.Provider>
    </AAStateContext.Provider>
  );
};

AAProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAAState = () => {
  const context = useContext(AAStateContext);
  if (context === undefined) {
    throw new Error('useAAState must be used within AAProvider');
  }
  return context;
};

export const useAADispatch = () => {
  const context = useContext(AADispatchContext);
  if (context === undefined) {
    throw new Error('useAADispatch must be used within AAProvider');
  }
  return context;
};
